import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Container, Row, Col, Card, Button, Form, Spinner, InputGroup, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FiChevronLeft,
  FiChevronRight,
  FiTrash2,
  FiSave,
  FiCalendar,
  FiTrendingUp,
  FiDollarSign,
  FiLayers,
  FiCheckCircle,
} from "react-icons/fi";
import moment from "moment";

import * as api from "../../api/getapi";
import * as postapi from "../../api/postapi";

const EXPENSE_FIELDS = [
  { key: "ad_spend", label: "Ad Spend", short: "Ad", color: "#3B82F6" },
  { key: "delivery_cost", label: "Delivery", short: "Del", color: "#F59E0B" },
  { key: "packaging", label: "Packaging", short: "Pkg", color: "#8B5CF6" },
  { key: "wht_rate", label: "WHT %", short: "WHT%", color: "#EF4444", isPercentage: true },
  { key: "return_percentage", label: "Return %", short: "Ret%", color: "#F43F5E", isPercentage: true },
  { key: "product_cost", label: "Product", short: "Prod", color: "#059669" },
];

const formatPKR = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("en-PK", { maximumFractionDigits: 0 });
};

const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className="d-flex align-items-center gap-3 py-3 px-3">
      <div
        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 44, height: 44, backgroundColor: `${accent}15`, color: accent }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: "0.7rem", letterSpacing: 0.5 }}>
          {label}
        </div>
        <div className="fw-bold" style={{ fontSize: "1.15rem", color: accent }}>
          {value}
        </div>
        {sub && <div className="text-muted" style={{ fontSize: "0.72rem" }}>{sub}</div>}
      </div>
    </Card.Body>
  </Card>
);

const DailyExpenses = () => {
  const [cursor, setCursor] = useState(moment().startOf("month"));
  const [rowsByDate, setRowsByDate] = useState({});
  const [draftByDate, setDraftByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState(null);
  const [jumpDate, setJumpDate] = useState("");
  const [roasByDate, setRoasByDate] = useState({});

  const monthStartStr = cursor.clone().startOf("month").format("YYYY-MM-DD");
  const monthEndStr = cursor.clone().endOf("month").format("YYYY-MM-DD");
  const monthLabel = cursor.format("MMMM YYYY");

  const days = useMemo(() => {
    const arr = [];
    const c = moment(monthStartStr);
    const end = moment(monthEndStr);
    while (c.isSameOrBefore(end, "day")) {
      arr.push(c.clone());
      c.add(1, "day");
    }
    return arr;
  }, [monthStartStr, monthEndStr]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const [res, roasRes] = await Promise.all([
        api.getDailyExpenses({ dateFrom: monthStartStr, dateTo: monthEndStr }),
        api.getROASReport({ dateFrom: monthStartStr, dateTo: monthEndStr, revenueBasis: "delivered" }).catch(() => null),
      ]);
      const map = {};
      (res.data || []).forEach((r) => {
        map[moment(r.expense_date).format("YYYY-MM-DD")] = r;
      });
      setRowsByDate(map);
      setDraftByDate({});
      // Build ROAS lookup: date → { wht_cost, cod_delivered_revenue, delivery_cost_on_returns, net_product_cost }
      const roasMap = {};
      if (roasRes?.data) {
        roasRes.data.forEach((r) => {
          roasMap[r.date] = r;
        });
      }
      setRoasByDate(roasMap);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [monthStartStr, monthEndStr]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const getDraft = (dateKey) => {
    if (draftByDate[dateKey]) return draftByDate[dateKey];
    const saved = rowsByDate[dateKey];
    const base = {
      ad_spend: "",
      delivery_cost: "",
      packaging: "",
      wht_rate: "",
      return_percentage: "",
      product_cost: "",
      notes: "",
    };
    if (!saved) return base;
    return {
      ad_spend: saved.ad_spend ?? "",
      delivery_cost: saved.delivery_cost ?? "",
      packaging: saved.packaging ?? "",
      wht_rate: saved.wht_rate ?? "",
      return_percentage: saved.return_percentage ?? "",
      product_cost: saved.product_cost ?? "",
      notes: saved.notes ?? "",
    };
  };

  const handleFieldChange = (dateKey, field, value) => {
    setDraftByDate((prev) => ({
      ...prev,
      [dateKey]: { ...getDraft(dateKey), [field]: value },
    }));
  };

  const isDirty = (dateKey) => Boolean(draftByDate[dateKey]);

  const handleSave = async (dateKey) => {
    const draft = getDraft(dateKey);
    const payload = {
      expense_date: dateKey,
      ad_spend: Number(draft.ad_spend) || 0,
      delivery_cost: Number(draft.delivery_cost) || 0,
      packaging: Number(draft.packaging) || 0,
      wht_rate: Number(draft.wht_rate) || 0,
      return_percentage: Number(draft.return_percentage) || 0,
      product_cost: Number(draft.product_cost) || 0,
      notes: draft.notes || null,
    };
    setSavingDate(dateKey);
    try {
      await postapi.upsertDailyExpense(payload);
      toast.success(`Saved expenses for ${moment(dateKey).format("DD MMM YYYY")}`);
      await fetchExpenses();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to save");
    } finally {
      setSavingDate(null);
    }
  };

  const handleDelete = async (dateKey) => {
    if (!window.confirm(`Delete expenses for ${moment(dateKey).format("DD MMM YYYY")}?`)) return;
    try {
      await postapi.deleteDailyExpense(dateKey);
      toast.success(`Deleted expenses for ${moment(dateKey).format("DD MMM YYYY")}`);
      await fetchExpenses();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to delete");
    }
  };

  const rowTotal = (dateKey) => {
    const d = getDraft(dateKey);
    return EXPENSE_FIELDS.reduce((sum, f) => {
      if (f.isPercentage) return sum; // return_percentage is not a cost
      return sum + (Number(d[f.key]) || 0);
    }, 0);
  };

  // Calculated PKR impact for percentage fields, shown inline under inputs.
  // WHT cost uses cod_delivered_revenue from the ROAS report (backend).
  // Return cost impact = return% × (delivery_cost + product_cost) — locally computed.
  const calcWhtCost = (dateKey) => {
    const roas = roasByDate[dateKey];
    if (!roas) return 0;
    const d = getDraft(dateKey);
    const rate = Number(d.wht_rate) || 0;
    return (Number(roas.cod_delivered_revenue) || 0) * (rate / 100);
  };

  const calcReturnCostImpact = (dateKey) => {
    const d = getDraft(dateKey);
    const returnPct = Number(d.return_percentage) || 0;
    if (returnPct === 0) return 0;
    const delivery = Number(d.delivery_cost) || 0;
    const product = Number(d.product_cost) || 0;
    // Wasted delivery + lost product value
    return delivery * (returnPct / 100) + product * (returnPct / 100);
  };

  const hasData = (dateKey) => {
    const d = getDraft(dateKey);
    return EXPENSE_FIELDS.some((f) => Number(d[f.key]) > 0);
  };

  const monthTotals = useMemo(() => {
    const totals = {
      ad_spend: 0,
      delivery_cost: 0,
      packaging: 0,
      wht_rate: 0,
      return_percentage: 0,
      product_cost: 0,
      total: 0,
      daysWithData: 0,
    };
    days.forEach((d) => {
      const key = d.format("YYYY-MM-DD");
      const draft = getDraft(key);
      let dayHasData = false;
      EXPENSE_FIELDS.forEach((f) => {
        const v = Number(draft[f.key]) || 0;
        if (f.isPercentage) {
          if (v > 0) dayHasData = true;
          return; // tracked separately as average below
        }
        totals[f.key] += v;
        if (v > 0) dayHasData = true;
      });
      totals.return_percentage = Math.max(totals.return_percentage, Number(draft.return_percentage) || 0);
      totals.total += rowTotal(key);
      if (dayHasData) totals.daysWithData++;
    });
    return totals;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, rowsByDate, draftByDate]);

  const todayKey = moment().format("YYYY-MM-DD");
  const isCurrentMonth = cursor.isSame(moment(), "month");

  const handleJumpToDate = () => {
    if (!jumpDate) return;
    const target = moment(jumpDate);
    if (!target.isValid()) return;
    setCursor(target.clone().startOf("month"));
    setJumpDate("");
    // Scroll to the date after the table renders
    setTimeout(() => {
      const el = document.getElementById(`expense-row-${target.format("YYYY-MM-DD")}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
  };

  return (
    <Container fluid className="px-xl-4 py-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0 fw-bold">Marketplace Expenses</h4>
          <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
            Track daily costs (ad spend, delivery, packaging, WHT, returns, product) to compute ROAS &amp; net profit.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Back-date jump */}
          <InputGroup style={{ width: "auto" }}>
            <InputGroup.Text className="bg-white">
              <FiCalendar size={14} />
            </InputGroup.Text>
            <Form.Control
              type="date"
              size="sm"
              value={jumpDate}
              max={todayKey}
              onChange={(e) => setJumpDate(e.target.value)}
              style={{ width: 150 }}
            />
            <Button size="sm" variant="outline-primary" onClick={handleJumpToDate} disabled={!jumpDate}>
              Go
            </Button>
          </InputGroup>
        </div>
      </div>

      {/* Summary cards */}
      <Row className="g-2 mb-3">
        <Col xs="6" md="3">
          <SummaryCard
            icon={<FiTrendingUp size={20} />}
            label="Ad Spend"
            value={`Rs ${formatPKR(monthTotals.ad_spend)}`}
            sub={monthTotals.daysWithData > 0 ? `Avg Rs ${formatPKR(monthTotals.ad_spend / monthTotals.daysWithData)}/day` : "No data yet"}
            accent="#3B82F6"
          />
        </Col>
        <Col xs="6" md="3">
          <SummaryCard
            icon={<FiDollarSign size={20} />}
            label="Total Expenses"
            value={`Rs ${formatPKR(monthTotals.total)}`}
            sub={`${monthTotals.daysWithData} day${monthTotals.daysWithData !== 1 ? "s" : ""} with data`}
            accent="#EF4444"
          />
        </Col>
        <Col xs="6" md="3">
          <SummaryCard
            icon={<FiLayers size={20} />}
            label="Product Cost"
            value={`Rs ${formatPKR(monthTotals.product_cost)}`}
            sub="Largest cost category"
            accent="#059669"
          />
        </Col>
        <Col xs="6" md="3">
          <SummaryCard
            icon={<FiCheckCircle size={20} />}
            label="Days Tracked"
            value={`${monthTotals.daysWithData} / ${days.length}`}
            sub={isCurrentMonth ? "This month in progress" : monthLabel}
            accent="#8B5CF6"
          />
        </Col>
      </Row>

      {/* Month navigation */}
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <h6 className="mb-0 fw-bold text-muted">
          <FiCalendar size={16} className="me-1" /> {monthLabel}
        </h6>
        <InputGroup style={{ width: "auto" }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setCursor(cursor.clone().subtract(1, "month"))}>
            <FiChevronLeft /> Prev
          </Button>
          {!isCurrentMonth && (
            <Button variant="outline-primary" size="sm" onClick={() => setCursor(moment().startOf("month"))}>
              This Month
            </Button>
          )}
          <Button variant="outline-secondary" size="sm" onClick={() => setCursor(cursor.clone().add(1, "month"))}>
            Next <FiChevronRight />
          </Button>
        </InputGroup>
      </div>

      {/* Expense table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: "70vh" }}>
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr>
                    <th style={{ minWidth: 100 }} className="fw-semibold">Date</th>
                    {EXPENSE_FIELDS.map((f) => (
                      <th key={f.key} className="text-end fw-semibold" style={{ minWidth: 105 }}>
                        <span style={{ color: f.color }}>{f.label}</span>
                      </th>
                    ))}
                    <th className="text-end fw-semibold" style={{ minWidth: 120, backgroundColor: "#F8FAFC" }}>
                      Total
                    </th>
                    <th style={{ minWidth: 80 }} className="fw-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => {
                    const key = d.format("YYYY-MM-DD");
                    const isToday = key === todayKey;
                    const isPast = d.isBefore(moment(), "day");
                    const isWeekend = d.day() === 0 || d.day() === 6;
                    const dirty = isDirty(key);
                    const total = rowTotal(key);
                    const saved = rowsByDate[key];
                    const dayHasData = hasData(key);

                    return (
                      <tr
                        key={key}
                        id={`expense-row-${key}`}
                        className={isToday ? "table-warning" : isWeekend ? "table-light" : ""}
                        style={{ opacity: !isPast && !isToday && !dayHasData ? 0.6 : 1 }}
                      >
                        <td className="py-2">
                          <div className="d-flex align-items-center gap-2">
                            <div>
                              <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {d.format("DD")}
                                <span className="text-muted ms-1" style={{ fontSize: "0.75rem" }}>
                                  {d.format("MMM")}
                                </span>
                              </div>
                              <small className="text-muted" style={{ fontSize: "0.68rem" }}>
                                {d.format("ddd")}
                              </small>
                            </div>
                            {isToday && (
                              <Badge pill bg="primary" style={{ fontSize: "0.6rem" }}>
                                Today
                              </Badge>
                            )}
                            {saved && !dirty && (
                              <FiCheckCircle size={12} className="text-success" title="Saved" />
                            )}
                          </div>
                        </td>
                        {EXPENSE_FIELDS.map((f) => (
                          <td key={f.key} className="p-1">
                            <div className="position-relative">
                              <Form.Control
                                type="number"
                                min="0"
                                max={f.isPercentage ? 100 : undefined}
                                step={f.isPercentage ? "0.01" : undefined}
                                size="sm"
                                className="text-end border-0"
                                placeholder="0"
                                style={{ fontSize: "0.8rem", paddingRight: f.isPercentage ? 18 : undefined }}
                                value={getDraft(key)[f.key]}
                                onChange={(e) => handleFieldChange(key, f.key, e.target.value)}
                                onBlur={() => dirty && handleSave(key)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.currentTarget.blur();
                                }}
                              disabled={savingDate === key}
                            />
                              {f.isPercentage && (
                                <span
                                  className="position-absolute end-0 top-50 translate-middle-y text-muted pe-1"
                                  style={{ fontSize: "0.7rem", pointerEvents: "none" }}
                                >
                                  %
                                </span>
                              )}
                            </div>
                            {f.isPercentage ? (
                              <div className="text-end text-muted" style={{ fontSize: "0.65rem", lineHeight: 1.2, height: 14, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden" }}>
                                {(() => {
                                  const calc = f.key === "wht_rate" ? calcWhtCost(key) : calcReturnCostImpact(key);
                                  return calc > 0 ? `= Rs ${formatPKR(calc)}` : "";
                                })()}
                              </div>
                            ) : (
                              <div style={{ height: 14, marginTop: 2 }} />
                            )}
                          </td>
                        ))}
                        <td className="text-end fw-bold py-2" style={{ backgroundColor: "#F8FAFC" }}>
                          {savingDate === key ? (
                            <Spinner size="sm" animation="border" />
                          ) : total > 0 ? (
                            <span style={{ color: total > 0 ? "#1E293B" : "#94A3B8" }}>
                              {formatPKR(total)}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="p-1">
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant={dirty ? "primary" : "outline-light"}
                              disabled={!dirty || savingDate === key}
                              onClick={() => handleSave(key)}
                              title="Save"
                              style={{ width: 30, height: 30, padding: 0 }}
                            >
                              <FiSave size={13} />
                            </Button>
                            {saved && (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDelete(key)}
                                title="Delete"
                                style={{ width: 30, height: 30, padding: 0 }}
                              >
                                <FiTrash2 size={13} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="table-light" style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
                  <tr className="fw-bold">
                    <td className="py-2">Month Total</td>
                    {EXPENSE_FIELDS.map((f) => (
                      <td key={f.key} className="text-end py-2" style={{ color: f.color }}>
                        {f.isPercentage
                          ? `${monthTotals[f.key] > 0 ? monthTotals[f.key].toFixed(2) : "0"}%`
                          : formatPKR(monthTotals[f.key])}
                      </td>
                    ))}
                    <td className="text-end py-2" style={{ backgroundColor: "#F1F5F9", fontSize: "0.9rem" }}>
                      Rs {formatPKR(monthTotals.total)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.78rem" }}>
        Enter costs in PKR. Values auto-save on blur or Enter. Use the date picker above to jump to any day for back-dated entry.
        ROAS &amp; Net Profit are visualized on the Marketplace Dashboard.
      </p>
    </Container>
  );
};

export default DailyExpenses;
